import { Injectable } from '@angular/core';
import { WavesConnectorService } from '@services/waves-connector.service';
import { map, Observable } from 'rxjs';
import BN from '@utils/bn';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import * as plutoActions from '@stores/pluto.actions';
import { environment } from '../environments/environment';

interface IWavesCapResponse {
  time: string;
  weightedAveragePrice: number;
  open?: number;
  close?: number;
  volume?: number;
  quoteVolume?: number;
  txsCount?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PlutoOnWavesService {
  constructor(
    private _wavesConnectorService: WavesConnectorService,
    private _httpClient: HttpClient,
    private _store: Store,
  ) {}

  private _wavesCapApiUrl = environment.wavesCapApiUrl;

  public isLoggedIn$: Observable<boolean> =
    this._wavesConnectorService.isLoggedIn$;

  private PLUTO_ASSET_ID: string =
    'Ajso6nTTjptu2UHLx6hfSXVtHFtRBJCkKYd5SAyj7zf5';

  private PLUTO_DAPP_ADDRESS: string = '3P7dGTVZp8VLDYy3XEaUQbiqfi9cMK1Ly5q';

  private PLUTO_WAVESCAP_DAPP_ADDRESS: string =
    '3PGFHzVGT4NTigwCKP1NcwoXkodVZwvBuuU';

  public PLUTO_DECIMALS: number = 8;

  public TRANSACTION_FEE: number = 500000;

  public updateBalances() {
    this._wavesConnectorService.updateBalances();
  }

  public getWalletBalance$(): Observable<BN> {
    return this._wavesConnectorService.getAssetBalance$(this.PLUTO_ASSET_ID);
  }

  public getStakedBalance$(): Observable<BN> {
    return this._wavesConnectorService.getStakedBalance$(
      this.PLUTO_DAPP_ADDRESS,
    );
  }

  public stake(value: number | BN): Promise<string | void> {
    return this._wavesConnectorService.invoke(
      {
        fee: this.TRANSACTION_FEE,
        dApp: this.PLUTO_DAPP_ADDRESS,
        payment: [
          {
            assetId: this.PLUTO_ASSET_ID,
            amount: BN.parseUnits(value, this.PLUTO_DECIMALS).toNumber(),
          },
        ],
        call: {
          function: 'stake',
          args: [],
        },
      },
      () => this._store.dispatch(plutoActions.stakeSended()),
    );
  }

  public unstake(value: number | BN): Promise<string | void> {
    return this._wavesConnectorService.invoke(
      {
        fee: this.TRANSACTION_FEE,
        dApp: this.PLUTO_DAPP_ADDRESS,
        payment: [],
        call: {
          function: 'unstake',
          args: [
            {
              type: 'integer',
              value: BN.parseUnits(value, this.PLUTO_DECIMALS).toString(),
            },
          ],
        },
      },
      () => {
        this._store.dispatch(plutoActions.unstakeSended());
      },
    );
  }

  public getPlutoPriceChartData$(
    duration: string = '1y',
  ): Observable<{ [key: string]: number }> {
    return this._httpClient
      .get<{
        data: { data: IWavesCapResponse }[];
      }>(
        this._wavesCapApiUrl +
          '/chart/dapp/' +
          this.PLUTO_WAVESCAP_DAPP_ADDRESS +
          '-swap-WAVES-' +
          this.PLUTO_ASSET_ID +
          '-' +
          duration +
          '.json',
      )
      .pipe(
        map<{ data: { data: IWavesCapResponse }[] }, { [key: string]: number }>(
          (data) =>
            data.data.reduce(
              (
                previousValue: { [key: string]: number },
                currentValue: { data: IWavesCapResponse },
              ): { [key: string]: number } => ({
                ...previousValue,
                [currentValue.data.time]: new BN(1)
                  .div(currentValue.data.weightedAveragePrice)
                  .toNumber(),
              }),
              {} as { [key: string]: number },
            ),
        ),
      );
  }
}
